import { SortDirection } from '@factorialco/deprecated-design-system'
import { orderBy } from 'lodash'
import { makeAutoObservable } from 'mobx'
import moment from 'moment'
import _ from 'lodash'
import { matchText } from 'modules/core/lib/stringUtils'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import levels from 'modules/job_catalog/store/collections/levels'
import roles from 'modules/job_catalog/store/collections/roles'
import type JobRole from 'modules/job_catalog/store/models/JobRole'

import { sort as AnalyticsSort } from './Analytics/sortAnalytics'
import { SORTABLE_ATTRIBUTES as ANALYTICS_SORTABLE_ATTRIBUTES } from './Analytics/sortAnalytics'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'

export const SORTABLE_ATTRIBUTES = [
  'name',
  'employeesQuantity',
  ...ANALYTICS_SORTABLE_ATTRIBUTES,
] as const
export type RolesSorteableAttribute = (typeof SORTABLE_ATTRIBUTES)[number]

const sortContractVersionsByEffectiveOn = (models: ContractVersion[]) => {
  return _.sortBy(models, (cv: ContractVersion) => cv.effectiveOn)
}

// Returns the subset of `models` (which should be the contract versions for
// an employee) that were effective (aka "current") between `startDate` and
// `endDate`.
const filterContractVersionsByCurrentWithinDateRange = (
  models: ContractVersion[],
  startDate: moment.Moment,
  endDate: moment.Moment
): ContractVersion[] => {
  const sortedModels = sortContractVersionsByEffectiveOn(models)

  return sortedModels.filter(
    (model, index) =>
      model.effectiveOn.isSameOrBefore(endDate, 'day') &&
      (index === sortedModels.length - 1 || // last one?
        sortedModels[index + 1]?.effectiveOn.isAfter(startDate, 'day'))
  )
}

const currentContractVersionsWithinDateRange = (
  startDate: moment.Moment,
  endDate: moment.Moment
) => {
  return _.chain(contractVersions.models)
    .groupBy((c) => c.attributes.employee_id)
    .map((groupedByEmployee) =>
      filterContractVersionsByCurrentWithinDateRange(groupedByEmployee, startDate, endDate)
    )
    .flatten()
    .value()
}
function sortedValue(role: JobRole, attribute: RolesSorteableAttribute) {
  if (attribute === 'name') {
    return role.attributes.name
  }

  if (attribute === 'employeesQuantity') {
    return store.employeesCountByJobRoleId(role.id)
  }

  return AnalyticsSort(role.id, attribute)
}

const currentContractVersions = () => {
  const date = moment()
  return currentContractVersionsWithinDateRange(date, date)
}

const sortedRoles = (
  jobRoles: JobRole[],
  attribute: RolesSorteableAttribute,
  direction: SortDirection
): JobRole[] => {
  return orderBy(jobRoles, (role) => sortedValue(role, attribute), [direction])
}

class RolesTableStore {
  constructor() {
    makeAutoObservable(this)
  }

  get employeesByJobRoles() {
    const contracts = currentContractVersions()
    const activeEmployeeSet = new Set(employees.active.map((emp) => emp.attributes.id))
    const employeeCountsPerJobRole: Record<string, number> = {}
    const visitedEmployees = new Set<string>()

    contracts.forEach((contract) => {
      if (visitedEmployees.has(contract.attributes.employee_id)) {
        return
      }

      if (!activeEmployeeSet.has(contract.attributes.employee_id)) {
        return
      }

      const level = levels.get(contract.attributes.job_catalog_level_id)

      if (!level) {
        return
      }

      employeeCountsPerJobRole[level.attributes.role_id] =
        (employeeCountsPerJobRole[level.attributes.role_id] ?? 0) + 1

      visitedEmployees.add(contract.attributes.employee_id)
    })

    return employeeCountsPerJobRole
  }

  employeesCountByJobRoleId(jobRoleId: string) {
    return this.employeesByJobRoles[jobRoleId] || 0
  }

  get jobRoles() {
    return roles.active
  }

  filteredJobRoles(
    search: string,
    sortAttribute: RolesSorteableAttribute,
    sortDirection: SortDirection
  ) {
    const filteredByNameRoles = this.jobRoles.filter((x) => matchText(search, x.attributes.name))
    return sortedRoles(filteredByNameRoles, sortAttribute, sortDirection)
  }
}

const store = new RolesTableStore()
export default store
